import { useQuery } from '@tanstack/react-query'
import patientHttp, { PatientApiError } from '@/lib/patient-http'
import type { CareTrendQuery, CareTrendResponse } from '@/types/care'

/** What a patient with no care history resolves to — an empty series, not a failed read. */
const EMPTY_TREND: CareTrendResponse = {
  data: [],
  pagination: { page: 1, limit: 0, total: 0, totalPages: 0 }
}

/** One page is the window the chart draws. Newest-first, so the window is the recent one. */
const PAGE_SIZE = 500

/**
 * A patient's care check-in history, oldest first — the series behind the care timeline.
 *
 * `desc`, despite the chart plotting left to right: one page is all this fetches, and a patient
 * with more check-ins than fit in it would otherwise return the *oldest* page — silently hiding
 * every recent check-in, including the one the table below the chart describes. Newest-first makes
 * the page the recent window instead. Nothing downstream depends on the order: `toCareTimeline`
 * and `toSectionSummaries` both sort by date themselves, and the latest check-in is picked by date
 * rather than by position.
 *
 * `includeAnswers` is on: the table under the chart lists the symptoms selected in each section,
 * which the scores alone cannot say. That detail is special-category health data, which is why the
 * endpoint keeps it behind a flag rather than sending it by default — so it is requested here,
 * where it is displayed, and nowhere else.
 */
export const useCareTrend = (patientHash: string | undefined, query: CareTrendQuery = {}) => {
  return useQuery({
    queryKey: ['careTrend', patientHash, query],
    // No base URL means no Patient API configured for this environment. Without this guard axios
    // would resolve the path against PA's own origin, where `vercel.json` rewrites everything to the
    // SPA — handing back `index.html` with a 200 to be parsed as a trend. Disabled, the panel shows
    // its empty state (or sample data, where opted in) instead.
    enabled: !!patientHash && !!import.meta.env.VITE_PATIENT_API_URL,
    queryFn: async () => {
      try {
        return await patientHttp.get<CareTrendResponse, CareTrendResponse>(`/care/patients/${patientHash}/trend`, {
          params: {
            order: 'desc',
            includeAnswers: true,
            limit: PAGE_SIZE,
            ...query
          }
        })
      } catch (error) {
        // The endpoint answers 404 for a patient who has never been enrolled in a care module.
        // That is an answer — an empty series — so it resolves, and the panel shows its "no care
        // consultations yet" state rather than "care data could not be loaded".
        if (error instanceof PatientApiError && error.status === 404) return EMPTY_TREND
        throw error
      }
    },
    // A check-in arrives at most once a day; re-reading it while the prescriber works is waste.
    staleTime: 1000 * 60 * 10,
    // A 4xx is an answer about the request, not a transient fault — only server and network errors
    // are worth trying again.
    retry: (failureCount, error) => {
      const status = error instanceof PatientApiError ? error.status : undefined
      if (status && status >= 400 && status < 500) return false
      return failureCount < 2
    }
  })
}
